import { type Moment } from "moment/moment"
import { useOrderStore } from "@/store/orders.store"
import { useSaleStore } from "@/store/sale.store"
import { useRentalDispenseStore } from "@/store/rental-dispense.store"
import { useOrdersAPIFunctions } from "@/composables/api/orders-api-functions"
import { storeToRefs } from "pinia"
import { GenericModelController } from "@/controllers/model-controllers/abstract/generic-model-controller"
import { type IOrder } from "@/interfaces/api/order.interface"
import { Order } from "@/models/order.model"
import { OrderRepository } from "@/repositories/order-repository"
import { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model.ts"
import moment from "moment"
import { ODataQueryBuilder } from "@/builder/odata-query-builder.ts"
import { useSynchronisationStore } from "@/store/synchronisation.store.ts"
import { watch } from "vue"
import { type IRepositoryRequestConfig } from "@/interfaces/repository/repository-request-config.interface"

export default class OrderController extends GenericModelController<
  Order,
  IOrder,
  OrderRepository
> {
  private static readonly missingOrderBatchSize = 100

  private orderStore = useOrderStore()
  private synchronisationStore = useSynchronisationStore()
  private missingOrderRetrievals = new Map<
    string,
    { promise: Promise<Order | undefined>; resolve: (order: Order | undefined) => void }
  >()
  private pendingMissingOrderIds = new Set<string>()
  private missingOrderFlushScheduled = false
  private isFlushingMissingOrders = false

  constructor() {
    const orderStore = useOrderStore()
    const { orders } = storeToRefs(orderStore)

    const storeFunctionsConfig = {
      collection: orders
    }

    super(Order, storeFunctionsConfig, new OrderRepository())

    watch(
      () => this.synchronisationStore.isApplyingBulk,
      (isApplyingBulk, wasApplyingBulk) => {
        if (wasApplyingBulk && !isApplyingBulk) {
          // As soon as the bulk is completed, flush any pending missing orders.
          this.scheduleMissingOrderFlush()
        }
      }
    )
  }

  /**
   * Fetches orders and adds only orders that are not already managed by the store.
   * Returning the stored instances keeps callers in sync with the canonical order state.
   */
  public override async getList(requestConfig?: IRepositoryRequestConfig): Promise<Order[]> {
    const data = await this.apiFunctions.getModels(requestConfig)
    await this.orderStore.pushNonExistentOrders(data.map((order) => new Order(order)))

    const ordersById = new Map(this.orderStore.orders.map((order) => [order.id, order]))
    return data
      .map((order) => ordersById.get(order.id))
      .filter((order): order is Order => order !== undefined)
  }

  /**
   * Retrieve an order referenced by an incoming socket message.
   *
   * Requests for the same order share one promise. During socket recovery,
   * requests are collected until the recovered message range has been applied,
   * then fetched in bounded `id in (...)` batches.
   */
  public retrieveMissingOrder(orderId: string): Promise<Order | undefined> {
    const existingOrder = this.orderStore.orders.find((order) => order.id === orderId)
    if (existingOrder) {
      return Promise.resolve(existingOrder)
    }

    const existingRetrieval = this.missingOrderRetrievals.get(orderId)
    if (existingRetrieval) {
      return existingRetrieval.promise
    }

    let resolve!: (order: Order | undefined) => void
    const promise = new Promise<Order | undefined>((promiseResolve) => {
      resolve = promiseResolve
    })

    this.missingOrderRetrievals.set(orderId, { promise, resolve })
    this.pendingMissingOrderIds.add(orderId)
    this.scheduleMissingOrderFlush()

    return promise
  }

  /**
   * Retrieves orders that contain instructions with one of the supplied sales
   * or rental dispenses.
   *
   * Sales and rental dispenses that are already in their store are left out of the
   * filter: they are only reachable through an instruction that has its administrative
   * events populated, so both the order and its events are already held locally. When
   * every supplied id is already present, no request is made at all.
   *
   * Note that the already-present ids are dropped instead of the already-present ORDER
   * ids. An order can sit in the store while its instructions are still unpopulated —
   * events are loaded lazily — and excluding it would drop the very orders whose
   * instructions still have to be populated.
   */
  public async retrieveOrdersForAdministrativeEventIds(
    saleIds: string[],
    rentalDispenseIds: string[]
  ) {
    saleIds = OrderController.excludeLocallyPresentIds(saleIds, useSaleStore().sales)
    rentalDispenseIds = OrderController.excludeLocallyPresentIds(
      rentalDispenseIds,
      useRentalDispenseStore().rentalDispenses
    )

    if (saleIds.length === 0 && rentalDispenseIds.length === 0) {
      return []
    }

    const query = new ODataQueryBuilder().or("administrativeEvents", (orQuery) => {
      orQuery
        .if(saleIds.length > 0, (queryBuilder) => {
          queryBuilder.raw("sales", {
            instructions: { any: { sales: { any: { id: { in: saleIds } } } } }
          })
        })
        .if(rentalDispenseIds.length > 0, (queryBuilder) => {
          queryBuilder.raw("rentalDispenses", {
            instructions: {
              any: { rental_dispenses: { any: { id: { in: rentalDispenseIds } } } }
            }
          })
        })
    })

    return await this.getList({ query })
  }

  /**
   * The unique ids of `ids` that are not covered by `presentModels`.
   */
  private static excludeLocallyPresentIds(
    ids: string[],
    presentModels: readonly { id?: string }[]
  ): string[] {
    const missingIds = new Set(ids)
    if (missingIds.size === 0) {
      return []
    }

    for (const model of presentModels) {
      if (model.id) {
        missingIds.delete(model.id)
      }
    }

    return [...missingIds]
  }

  private scheduleMissingOrderFlush() {
    if (
      this.synchronisationStore.isApplyingBulk ||
      this.missingOrderFlushScheduled ||
      this.isFlushingMissingOrders ||
      this.pendingMissingOrderIds.size === 0
    ) {
      return
    }

    this.missingOrderFlushScheduled = true
    setTimeout(() => {
      this.missingOrderFlushScheduled = false
      void this.flushMissingOrderRetrievals()
    }, 0)
  }

  private async flushMissingOrderRetrievals() {
    if (this.isFlushingMissingOrders || this.synchronisationStore.isApplyingBulk) {
      return
    }

    this.isFlushingMissingOrders = true
    try {
      const orderIds = [...this.pendingMissingOrderIds]
      this.pendingMissingOrderIds.clear()

      for (let start = 0; start < orderIds.length; start += OrderController.missingOrderBatchSize) {
        await this.retrieveMissingOrderBatch(
          orderIds.slice(start, start + OrderController.missingOrderBatchSize)
        )
      }
    } finally {
      this.isFlushingMissingOrders = false
      this.scheduleMissingOrderFlush()
    }
  }

  private async retrieveMissingOrderBatch(orderIds: string[]) {
    try {
      const response = await new OrderRepository().getList({
        query: new ODataQueryBuilder().in("id", orderIds)
      })
      await this.orderStore.pushNonExistentOrders(response.data.map((order) => new Order(order)))
    } catch (error) {
      console.error("Could not retrieve orders referenced by socket messages", error)
    } finally {
      for (const orderId of orderIds) {
        const retrieval = this.missingOrderRetrievals.get(orderId)
        if (!retrieval) {
          continue
        }

        retrieval.resolve(this.orderStore.orders.find((order) => order.id === orderId))
        this.missingOrderRetrievals.delete(orderId)
      }
    }
  }

  /**
   * Fetches a list of orders that contain instructions for the specified service provider on the given date.
   * This method uses a set to ensure the same service provider–date combination is fetched only once,
   * preventing redundant API calls for previously requested combinations.
   */
  serviceProviderAndDateCombinations = new Set()

  public async fetchOrdersWithInstructionsForServiceProviderAndDate(
    serviceProvider: KnownBusinessEntityContact,
    date: Moment
  ) {
    if (
      this.serviceProviderAndDateCombinations.has(
        this.generateCombinationKey(serviceProvider, date)
      ) ||
      this.orderStore.dateIsAlreadyFetched(date)
    ) {
      return
    }
    this.serviceProviderAndDateCombinations.add(this.generateCombinationKey(serviceProvider, date))

    // Both conditions in ONE any(): the same instruction must match provider AND date.
    const query = new ODataQueryBuilder().raw("instructionForProviderOnDate", {
      instructions: {
        any: {
          service_provider_id: serviceProvider.id,
          date: date.format("YYYY-MM-DD")
        }
      }
    })

    return await this.getList({ query })
  }

  private generateCombinationKey(serviceProvider: KnownBusinessEntityContact, date: Moment) {
    return `${serviceProvider.id}-${date.format("YYYY-MM-DD")}`
  }

  public async fetchOrdersForSingleDate(date: Moment) {
    if (this.orderStore.dateIsAlreadyFetched(date)) {
      return
    }
    this.orderStore.onInitiateRequestForDateRange(date, date)

    try {
      const { retrieveOrdersDuringDateRange } = useOrdersAPIFunctions()
      const { orders } = storeToRefs(this.orderStore)

      const retrievedOrders = await retrieveOrdersDuringDateRange(
        date,
        date,
        orders.value.map((order) => order.id)
      )
      await this.orderStore.pushNonExistentOrders(retrievedOrders)
    } finally {
      this.orderStore.onRequestForDateRangeCompleted(date, date)
    }
  }

  /**
   * Fetches orders for a specified date range, ensuring that only dates that have not been fetched yet are processed.
   * @param dateStart
   * @param dateEnd
   */
  public async fetchOrdersForDateRange(dateStart: Moment, dateEnd: Moment) {
    // 1. collect all missing dates
    const missingDates: Moment[] = []
    const cursor = dateStart.clone()
    while (cursor.isSameOrBefore(dateEnd, "day")) {
      if (!this.orderStore.dateIsAlreadyFetched(cursor)) {
        missingDates.push(cursor.clone())
      }
      cursor.add(1, "day")
    }

    // nothing to do if all dates already fetched
    if (missingDates.length === 0) {
      return
    }

    // 2. group missing dates into contiguous [start,end] ranges
    type Range = { start: Moment; end: Moment }
    const ranges: Range[] = []
    let rangeStart = missingDates[0].clone()
    let previous = missingDates[0].clone()

    for (let i = 1; i < missingDates.length; i++) {
      const current = missingDates[i].clone()
      // if current is exactly one day after previous, extend the current range
      if (current.diff(previous, "days") === 1) {
        previous = current
      } else {
        // otherwise, close out the previous range and start a new one
        ranges.push({ start: rangeStart, end: previous })
        rangeStart = current
        previous = current
      }
    }
    // push the final range
    ranges.push({ start: rangeStart, end: previous })

    // 3. for each missing-range, invoke API + store updates
    const { retrieveOrdersDuringDateRange } = useOrdersAPIFunctions()
    const { orders } = storeToRefs(this.orderStore)

    for (const { start, end } of ranges) {
      this.orderStore.onInitiateRequestForDateRange(start, end)

      try {
        const retrieved = await retrieveOrdersDuringDateRange(
          start,
          end,
          orders.value.map((o) => o.id)
        )

        await this.orderStore.pushNonExistentOrders(retrieved)
      } finally {
        this.orderStore.onRequestForDateRangeCompleted(start, end)
      }
    }
  }

  /**
   * Fetches orders that are not already in the store. Uses a query that excludes orders with IDs that are already in
   * the store.
   */
  public async fetchNewOrders(query: ODataQueryBuilder = new ODataQueryBuilder()) {
    const { orders, numOrderRequestsInitiated, numOrderRequestsCompleted } = storeToRefs(
      this.orderStore
    )
    const existingOrderIds = orders.value.map((order) => order.id)

    numOrderRequestsInitiated.value++
    try {
      await this.getList({
        query: query.not("not", (qb) => {
          qb.in("id", existingOrderIds)
        })
      })
    } finally {
      numOrderRequestsCompleted.value++
    }
  }

  public async fetchInitialOrders(firstTime: boolean) {
    if (!firstTime) {
      return
    }

    const today = moment().startOf("day")
    const todayPlusOneWeek = today.clone().add(7, "days")

    this.orderStore.onInitiateRequestForDateRange(today, todayPlusOneWeek)
    try {
      const retrievedOrders = await this.retrieveTodayFutureEmptyAndUnpreparedOrders()

      await this.orderStore.pushNonExistentOrders(retrievedOrders)
    } finally {
      this.orderStore.onRequestForDateRangeCompleted(today, todayPlusOneWeek)
    }
  }

  private async retrieveTodayFutureEmptyAndUnpreparedOrders() {
    // const fourWeeksBack = moment()
    //   .subtract(4, "week").startOf("day").format("YYYY-MM-DD")

    const qb = new ODataQueryBuilder()
      // .or("rootOr", (subQbx) => {
      //   subQbx
      //   // Case A: There is at least one instruction with a date in the future (or today),
      //   // or the order is not prepared.
      //   .or("or1", (subQb) => {
      //     subQb
      //       .ge("instructions__date", moment().startOf("day").format("YYYY-MM-DD"))
      //       .eq("is_prepared", false)
      //     })
      //   // Case B: Otherwise, if there are no instructions, or only instructions with date = NULL, then only include
      //   // them if the order is recent (created within the last 4 weeks).
      //   .and("and", (subQb) => {
      //     subQb
      //       .or("or2", (subQb2) => {
      //         subQb2
      //           .eq("instructions__date", null)
      //           .eq("instructions", null)
      //       })
      //       .ge(
      //         "created_at", fourWeeksBack
      //       )
      //   })
      // })

      .or("or", (orQb) => {
        orQb
          .raw("futureInstruction", {
            instructions: { any: { date: { ge: moment().startOf("day").format("YYYY-MM-DD") } } }
          })
          .eq("is_prepared", false)
          // These last two arms are NOT interchangeable. Under the old relation traversal
          // `instructions__date eq null` also caught orders with no instructions at all,
          // because the LEFT JOIN supplied NULL columns. any() needs a real row, so the
          // "no instructions" case must be stated separately. Dropping it silently loses
          // every order without instructions (~64% of this result set on production).
          .raw("instructionWithoutDate", { instructions: { any: { date: null } } })
          .raw("noInstructionsAtAll", { not: { instructions: { any: {} } } })
      })

    const response = await new OrderRepository().getList({ query: qb })
    return response.data.map((order) => new Order(order))
  }
}
